import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_NAV_GROUP_EXPANDED } from './navigationSections'

/**
 * #1410 — group-level expand/collapse state for the admin sidebar rail.
 *
 * The rail is chunked into Admin / Hotels / Affiliates groups (see
 * navigationSections.ts). Each group header is a toggle; the open/closed
 * state is persisted per user in localStorage so it survives reloads and
 * navigation, and the defaults (Admin open, Hotels + Affiliates collapsed)
 * apply to any group the user has never toggled.
 *
 * Storage is best-effort: private mode, quota errors, or a locked-down
 * browser must never break the sidebar, so every localStorage access is
 * wrapped and falls back to the in-memory state.
 */

export const SIDEBAR_GROUPS_STORAGE_PREFIX = 'hoteliate.sidebarGroups.'

export type SidebarGroupOverrides = Partial<Record<string, boolean>>

export function sidebarGroupsStorageKey(userId: string | null | undefined): string {
  return SIDEBAR_GROUPS_STORAGE_PREFIX + (userId || 'anonymous')
}

export function readStoredGroupState(storageKey: string): SidebarGroupOverrides {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: SidebarGroupOverrides = {}
    for (const [group, open] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof open === 'boolean') out[group] = open
    }
    return out
  } catch {
    // Malformed JSON or unavailable storage — behave as if nothing was stored.
    return {}
  }
}

export function writeStoredGroupState(storageKey: string, state: SidebarGroupOverrides): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state))
  } catch {
    // Storage full / disabled — the in-memory state still drives the UI.
  }
}

/** Effective open state: the user's override if any, else the shipped default. */
export function resolveGroupOpen(group: string, overrides: SidebarGroupOverrides): boolean {
  const override = overrides[group]
  if (typeof override === 'boolean') return override
  const fallback = (DEFAULT_NAV_GROUP_EXPANDED as Record<string, boolean | undefined>)[group]
  return fallback ?? true
}

export interface SidebarGroupsApi {
  isGroupOpen: (group: string) => boolean
  toggleGroup: (group: string) => void
}

/**
 * @param userId   — namespaces the stored preference so two accounts on the
 *                   same browser (or an impersonation session) don't share it.
 * @param activeGroup — the group containing the current route, if any. When
 *                   the route moves into a collapsed group we open that group
 *                   so the active item is never hidden; the user can still
 *                   collapse it again afterwards.
 */
export function useSidebarGroups(
  userId: string | null | undefined,
  activeGroup: string | undefined,
): SidebarGroupsApi {
  const storageKey = sidebarGroupsStorageKey(userId)
  const [overrides, setOverrides] = useState<SidebarGroupOverrides>(() => readStoredGroupState(storageKey))

  // Re-read when the user (and therefore the storage key) changes.
  useEffect(() => {
    setOverrides(readStoredGroupState(storageKey))
  }, [storageKey])

  const setGroupOpen = useCallback(
    (group: string, open: boolean) => {
      setOverrides(prev => {
        if (resolveGroupOpen(group, prev) === open) return prev
        const next = { ...prev, [group]: open }
        writeStoredGroupState(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  useEffect(() => {
    if (activeGroup) setGroupOpen(activeGroup, true)
  }, [activeGroup, setGroupOpen])

  const isGroupOpen = useCallback((group: string) => resolveGroupOpen(group, overrides), [overrides])
  const toggleGroup = useCallback(
    (group: string) => setGroupOpen(group, !resolveGroupOpen(group, overrides)),
    [overrides, setGroupOpen],
  )

  return { isGroupOpen, toggleGroup }
}
